import { Box, Chip, Typography } from "@mui/material";
import useColors from "../../../../api/hooks/useColors";

// TODO Add full list of colors based on Figma:
// https://linear.app/breeze-security/issue/DEV-1552/add-full-list-of-colors-to-customlabel-and-change-all-labels-in-the
const CustomLabel = ({
  text,
  colorFamily,
  severity,
  labelType = "soft",
  size,
  onClick = undefined,
  icon = undefined,
  width = "auto",
}) => {
  const colors = useColors();

  const labelColors = {
    filled: {
      black: {
        default: {
          background: colors.grey800,
          color: colors.white,
          hover: colors.grey700,
        },
      },
      info: {
        light: {
          background: colors.info_light,
          color: colors.contrast_text,
          hover: colors.info_light_hover,
        },
        main: {
          background: colors.info_main,
          color: colors.contrast_text,
          hover: colors.info_12percent, // Change to other hover color
        },
        dark: {
          background: colors.info_dark,
          color: colors.contrast_text,
          hover: colors.info_12percent, // Change to other hover color
        },
        darker: {
          background: colors.info_darker,
          color: colors.contrast_text,
          hover: colors.info_12percent, // Change to other hover color
        },
      },
      NIST: {
        Identify: {
          background: colors.nist_identify_main,
          color: colors.text_primary,
        },
        Protect: {
          background: colors.nist_protect_main,
          color: colors.text_primary,
        },
        Detect: {
          background: colors.nist_detect_main,
          color: colors.text_primary,
        },
        Respond: {
          background: colors.nist_respond_main,
          color: colors.text_primary,
        },
        Recover: {
          background: colors.nist_recover_main,
          color: colors.text_primary,
        },
        Govern: {
          background: colors.nist_govern_main,
          color: colors.text_primary,
        },
      },
      primary: {
        light: {
          background: colors.primary_light,
          color: colors.common_white,
          hover: colors.primary_12percent,
        },
      },
    },
    outlined: {
      black: {
        default: {
          background: "transparent",
          color: colors.text_primary,
          hover: colors.grey700,
        },
      },
      info: {
        light: {
          background: "transparent",
          color: colors.info_light,
          hover: colors.info_light_hover,
        },
        main: {
          background: "transparent",
          color: colors.info_main,
          hover: colors.info_12percent, // Change to other hover color
        },
        dark: {
          background: "transparent",
          color: colors.info_dark,
          hover: colors.info_12percent, // Change to other hover color
        },
        darker: {
          background: "transparent",
          color: colors.info_darker,
          hover: colors.info_12percent, // Change to other hover color
        },
      },
      NIST: {
        Identify: {
          padding: "10px 0px",
          borderRadius: "12px",
          border: `1px solid ${colors.nist_identify_stroke}`,
          background: colors.common_white,
          color: colors.text_primary,
        },
        Protect: {
          padding: "10px 0px",
          borderRadius: "12px",
          border: `1px solid ${colors.nist_protect_stroke}`,
          background: colors.common_white,
          color: colors.text_primary,
        },
        Detect: {
          padding: "10px 0px",
          borderRadius: "12px",
          border: `1px solid ${colors.nist_detect_stroke}`,
          background: colors.common_white,
          color: colors.text_primary,
        },
        Respond: {
          padding: "10px 0px",
          borderRadius: "12px",
          border: `1px solid ${colors.nist_respond_stroke}`,
          background: colors.common_white,
          color: colors.text_primary,
        },
        Recover: {
          padding: "10px 0px",
          borderRadius: "12px",
          border: `1px solid ${colors.nist_recover_stroke}`,
          background: colors.common_white,
          color: colors.text_primary,
        },
        Govern: {
          padding: "10px 0px",
          borderRadius: "12px",
          border: `1px solid ${colors.nist_govern_stroke}`,
          background: colors.common_white,
          color: colors.text_primary,
        },
      },
    },
    soft: {
      black: {
        default: {
          background: colors.grey_8percent,
          color: colors.text_primary,
          hover: colors.grey200,
        },
      },
      info: {
        light: {
          background: colors.info_soft_light,
          color: colors.info_main,
          hover: colors.info_12percent, // Change to other hover color
        },
        main: {
          background: colors.info_16percent,
          color: colors.info_main,
          hover: colors.info_12percent, // Change to other hover color
        },
        dark: {
          background: colors.info_8percent,
          color: colors.info_dark,
          hover: colors.info_12percent, // Change to other hover color
        },
        darker: {
          background: colors.info_8percent,
          color: colors.info_darker,
          hover: colors.info_12percent, // Change to other hover color
        },
      },
      error: {
        light: {
          background: colors.error_soft_label_fill,
          color: colors.error_light_label_text,
          hover: colors.error_12percent,
        },
        main: {
          background: colors.error_16percent,
          color: colors.error_main,
        },
      },
      success: {
        light: {
          background: colors.success_8percent,
          color: colors.success_main,
          hover: colors.success_12percent,
        },
        main: {
          background: colors.success_16percent,
          color: colors.success_dark,
        },
      },
      warning: {
        main: {
          background: colors.warning_16percent,
          color: colors.warning_darker,
        },
      },
      secondary: {
        light: {
          background: colors.secondary_8percent,
          color: colors.secondary_dark,
          hover: colors.secondary_12percent,
        },
        dark: {
          background: colors.secondary_8percent,
          color: colors.secondary_darker,
          hover: colors.secondary_12percent,
        },
      },
      primary: {
        main: {
          background: colors.primary_8percent,
          color: colors.primary_main,
          hover: colors.primary_12percent,
        },
        dark: {
          background: colors.primary_16percent,
          color: colors.primary_400,
          hover: colors.primary_12percent,
        },
      },
    },
  };

  const getHeight = (size) => {
    switch (size) {
      case "xs":
        return "12px";
      case "small":
        return "18px";
      case "medium":
        return "24px";
      case "large":
        return "36px";
      default:
        return "18px";
    }
  };

  const style = {
    borderRadius:
      labelColors[labelType][colorFamily][severity].borderRadius || "6px",
    border: labelColors[labelType][colorFamily][severity].border || "none",
    background: labelColors[labelType][colorFamily][severity].background,
    color: labelColors[labelType][colorFamily][severity].color,
    padding: labelColors[labelType][colorFamily][severity].padding || "0px 0px",
    height: getHeight(size),
    width: width,
    userSelect: "none",
    ...(onClick && {
      "&:hover": {
        background: labelColors[labelType][colorFamily][severity].hover,
      },
    }),
  };

  return (
    <Chip
      label={
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="overline" sx={{ fontWeight: "400" }}>
            {text}
          </Typography>
          {icon}
        </Box>
      }
      clickable={onClick ? true : false}
      onClick={onClick ? onClick : undefined}
      sx={style}
    />
  );
};

export default CustomLabel;
